import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { tapHaptic } from '../services/native/hapticsService.js';

const PULL_RESISTANCE = 0.48;
const REFRESH_THRESHOLD_PX = 64;
const MAX_PULL_PX = 86;
const MIN_REFRESH_DISPLAY_MS = 550;

export function NativePullToRefresh({ scrollRef, onRefresh, enabled = true }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const distanceRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  const finishTimerRef = useRef(null);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!enabled || !scroller) return undefined;

    let startY = null;
    let thresholdHapticSent = false;
    let disposed = false;

    const updateDistance = (nextDistance) => {
      distanceRef.current = nextDistance;
      setPullDistance(nextDistance);
    };

    const resetGesture = () => {
      startY = null;
      thresholdHapticSent = false;
      if (!refreshingRef.current) updateDistance(0);
    };

    const startRefresh = () => {
      refreshingRef.current = true;
      setRefreshing(true);
      updateDistance(REFRESH_THRESHOLD_PX);

      Promise.resolve()
        .then(() => onRefreshRef.current?.())
        .catch(() => {})
        .finally(() => {
          if (disposed) return;
          finishTimerRef.current = window.setTimeout(() => {
            refreshingRef.current = false;
            setRefreshing(false);
            updateDistance(0);
          }, MIN_REFRESH_DISPLAY_MS);
        });
    };

    const onTouchStart = (event) => {
      if (refreshingRef.current || event.touches.length !== 1 || scroller.scrollTop > 0) return;
      startY = event.touches[0].clientY;
      thresholdHapticSent = false;
    };

    const onTouchMove = (event) => {
      if (startY === null || event.touches.length !== 1 || refreshingRef.current) return;
      if (scroller.scrollTop > 0) {
        resetGesture();
        return;
      }

      const deltaY = event.touches[0].clientY - startY;
      if (deltaY <= 0) {
        updateDistance(0);
        return;
      }

      if (event.cancelable) event.preventDefault();
      const nextDistance = Math.min(MAX_PULL_PX, deltaY * PULL_RESISTANCE);
      updateDistance(nextDistance);
      if (nextDistance >= REFRESH_THRESHOLD_PX && !thresholdHapticSent) {
        thresholdHapticSent = true;
        void tapHaptic();
      } else if (nextDistance < REFRESH_THRESHOLD_PX) {
        thresholdHapticSent = false;
      }
    };

    const onTouchEnd = () => {
      if (startY === null || refreshingRef.current) return;
      const shouldRefresh = distanceRef.current >= REFRESH_THRESHOLD_PX;
      resetGesture();
      if (shouldRefresh) startRefresh();
    };

    scroller.addEventListener('touchstart', onTouchStart, { passive: true });
    scroller.addEventListener('touchmove', onTouchMove, { passive: false });
    scroller.addEventListener('touchend', onTouchEnd, { passive: true });
    scroller.addEventListener('touchcancel', resetGesture, { passive: true });

    return () => {
      disposed = true;
      scroller.removeEventListener('touchstart', onTouchStart);
      scroller.removeEventListener('touchmove', onTouchMove);
      scroller.removeEventListener('touchend', onTouchEnd);
      scroller.removeEventListener('touchcancel', resetGesture);
      if (finishTimerRef.current !== null) window.clearTimeout(finishTimerRef.current);
    };
  }, [enabled, scrollRef]);

  if (!enabled) return null;

  const progress = Math.min(1, pullDistance / REFRESH_THRESHOLD_PX);
  return (
    <div
      className="native-pull-refresh"
      style={{
        '--native-pull-distance': `${pullDistance}px`,
        '--native-pull-opacity': progress,
        '--native-pull-rotation': `${Math.round(progress * 180)}deg`,
      }}
      aria-live="polite"
      data-refreshing={refreshing || undefined}
    >
      <span className={cn('native-pull-refresh-indicator', refreshing && 'is-refreshing')} aria-hidden="true">
        <RefreshCw size={19} strokeWidth={2.2} />
      </span>
      <span className="sr-only">{refreshing ? 'Refreshing page' : ''}</span>
    </div>
  );
}
